import { Injectable, inject, signal } from '@angular/core';
import { z } from 'zod';
import { kvGetJson, kvSetJson } from '@shared/lib/storage';
import { CHROME_API, KV_PREFIX } from '@shared/platform';

export type Theme = 'dark' | 'light';
export type VisualStyle = 'default' | 'editorial' | 'glass';

const ThemeSchema = z.enum(['dark', 'light']);

function parseJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly chrome = inject(CHROME_API, { optional: true });

  private chosen = false;

  readonly theme = signal<Theme>(this.loadSaved());
  readonly visualStyle = signal<VisualStyle>(this.loadVisualStyle());

  constructor() {
    void kvGetJson('theme', ThemeSchema).then(saved => {
      if (saved && !this.chosen) this.applyTheme(saved);
    });

    this.chrome?.storage.onChanged.addListener((changes, area) => {
      if (area !== 'local') return;
      const change = changes[KV_PREFIX + 'theme'];
      if (change?.newValue === undefined) return;
      const parsed = ThemeSchema.safeParse(parseJson(change.newValue));
      if (parsed.success) this.applyTheme(parsed.data);
    });

    globalThis.addEventListener('storage', event => {
      if (event.key !== 'theme') return;
      const parsed = ThemeSchema.safeParse(event.newValue);
      if (parsed.success) this.applyTheme(parsed.data);
    });
  }

  setTheme(theme: Theme): void {
    this.chosen = true;
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
    void kvSetJson('theme', theme);
  }

  private applyTheme(theme: Theme): void {
    if (this.theme() === theme && document.documentElement.dataset['theme'] === theme) return;
    this.theme.set(theme);
    document.documentElement.setAttribute('data-theme', theme);
  }

  toggle(): void {
    this.setTheme(this.theme() === 'dark' ? 'light' : 'dark');
  }

  setVisualStyle(style: VisualStyle): void {
    this.visualStyle.set(style);
    if (style === 'default') {
      document.documentElement.removeAttribute('data-style');
    } else {
      document.documentElement.setAttribute('data-style', style);
    }
    localStorage.setItem('visualStyle', style);
  }

  apply(): void {
    document.documentElement.setAttribute('data-theme', this.theme());
    const style = this.visualStyle();
    if (style !== 'default') {
      document.documentElement.setAttribute('data-style', style);
    }
  }

  private loadSaved(): Theme {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private loadVisualStyle(): VisualStyle {
    const saved = localStorage.getItem('visualStyle');
    if (saved === 'editorial' || saved === 'glass') return saved;
    return 'default';
  }
}
